'use client';

// Dead-nav-tap evidence — the mid-session companion to the connectivity store.
//
// The problem: when the radio is up but the internet is gone (WAN dead behind a
// live router, one-bar dead cellular, the iOS airplane-mode-but-WiFi-up lie),
// the connectivity store still reads "online" until something actually fails.
// So a user taps a bottom-nav tab, App Router kicks off a client navigation that
// fetches the route from the server… and it silently hangs/dies. Nothing tells
// the user; the tab just doesn't change.
//
// That dead tap IS the evidence. This module watches for it: after a tap we
// remember the current pathname and, if it hasn't changed within a short grace
// period, we run a REAL connectivity probe. Only if the probe confirms offline
// do we open the saved-work overlay — never off the timeout alone, so a merely
// slow-but-online navigation is left to complete. Probe spam is impossible: the
// connectivity store rate-limits every probe.

import { useCallback, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { probeConnectivity } from './connectivity';
import { openOfflineOverlay } from './overlay-events';

/** Grace period before a tap that hasn't moved the pathname counts as "dead". */
export const DEAD_NAV_PROBE_DELAY_MS = 1800;

export interface DeadNavWatchDeps {
  /** Reads the CURRENT pathname (re-evaluated when the timer fires). */
  getPathname: () => string;
  /** Runs a probe, resolving to the online verdict. Defaults to the store's. */
  probe?: () => Promise<boolean>;
  /** Opens the saved-work overlay. Defaults to the window-event dispatcher. */
  openOverlay?: () => void;
  /** Grace period override (tests). */
  delayMs?: number;
  /** Timer injection (tests / fake timers). */
  setTimer?: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clearTimer?: (h: ReturnType<typeof setTimeout>) => void;
}

/**
 * Arm a watch for a dead navigation away from `startPathname`. Returns a cancel
 * fn (call it on a fresh tap or on unmount). Pure and dependency-injected so it
 * unit-tests with fake timers and no React/DOM.
 */
export function armDeadNavWatch(startPathname: string, deps: DeadNavWatchDeps): () => void {
  const {
    getPathname,
    probe = probeConnectivity,
    openOverlay = openOfflineOverlay,
    delayMs = DEAD_NAV_PROBE_DELAY_MS,
    setTimer = setTimeout,
    clearTimer = clearTimeout,
  } = deps;

  const handle = setTimer(() => {
    // Navigation landed — the pathname moved. Nothing was dead; do nothing.
    if (getPathname() !== startPathname) return;
    // Still on the same path after the grace period: probe for the truth.
    void probe().then((isOnline) => {
      if (!isOnline) openOverlay();
    });
  }, delayMs);

  return () => clearTimer(handle);
}

/**
 * Hook form for MobileNav. Returns `arm()` — call it right before `router.push`
 * on a normal (believed-online) tab tap. If the push doesn't change the
 * pathname within the grace period and a probe then confirms offline, the
 * saved-work overlay opens automatically. A fresh tap cancels the prior watch;
 * unmount cancels too.
 */
export function useDeadNavProbe(): () => void {
  const pathname = usePathname();
  // Keep the latest pathname readable from inside the fired timer without
  // re-arming the watch on every render. Updated in a post-commit effect rather
  // than assigned during render: a render-phase ref write is unsound under
  // concurrent rendering, since React can discard a render but not the mutation
  // it made. Deferring costs nothing here because pathRef is only read from the
  // returned callback, which runs on a user tap — always after a commit.
  const pathRef = useRef(pathname);
  useEffect(() => {
    pathRef.current = pathname;
  });
  const cancelRef = useRef<null | (() => void)>(null);

  useEffect(() => () => { cancelRef.current?.(); }, []);

  return useCallback(() => {
    cancelRef.current?.();
    cancelRef.current = armDeadNavWatch(pathRef.current, {
      getPathname: () => pathRef.current,
    });
  }, []);
}
